/* Swatch -> card preview, and the solo-card guard.

   The colour row under a card is a set of links to sibling colourways, and
   clicking one swaps the card over to it (see the swatch click handler in
   tiger.js). Hovering one shows what that colourway looks like worn: the swatch
   carries its lifestyle shot in data-preview-src -- snippets/swatches.liquid
   picks it by the same alt-text rule the card itself uses -- and this fades it
   over the card for as long as the cursor rests there.

   None of the card's own state is touched. The preview is a separate plate on
   top of .pm-hover (see tiger-swatch-preview.css), so leaving the swatch
   restores the card exactly, and clicking through still lands on the real
   markup tiger.js swaps in.

   The first half of the file has nothing to do with swatches: it marks a card
   .pm-solo when it has no .pm-secondary to cross-fade to, so the stylesheet can
   hold its lead shot in place on hover instead of fading it out to nothing.
   That test is structural on purpose -- see the note in the stylesheet. */
(function () {
  'use strict';

  if (window.__tigerSwatchPreview) { return; }
  window.__tigerSwatchPreview = true;

  /* ---- the solo-card guard ---------------------------------------------- */

  function markSolo(media) {
    media.classList.toggle('pm-solo', !media.querySelector('.pm-secondary'));
  }

  function scanSolo(root) {
    if (!root || !root.querySelectorAll) { return; }
    Array.prototype.forEach.call(root.querySelectorAll('.product-media'), markSolo);
    if (root.matches && root.matches('.product-media')) { markSolo(root); }
  }

  scanSolo(document);

  /* tiger.js replaces .product-media's innerHTML wholesale when a swatch is
     clicked, so the card that arrives may be solo where the last one was not.
     Catch those as they land rather than trying to hook the click. */
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        var media = t && t.closest ? t.closest('.product-media') : null;
        if (media) { markSolo(media); }
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j] && added[j].nodeType === 1) { scanSolo(added[j]); }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ---- the swatch preview ------------------------------------------------ */

  /* A preview is for a cursor that can rest somewhere. Touch gets the swipe
     track instead, and on a coarse pointer .pm-hover is not displayed at all.
     The guard above still runs there -- it is what keeps a solo card painted. */
  if (window.matchMedia && !window.matchMedia('(hover: hover)').matches) { return; }

  /* tiger.js already warms .pm-secondary when it scrolls into view, but a card
     hovered the moment it arrives can still be holding a lazy image. Asking for
     it on the way in costs nothing and takes the stutter off the cross-fade. */
  document.addEventListener('pointerover', function (e) {
    var item = e.target && e.target.closest ? e.target.closest('.product-item') : null;
    if (!item) { return; }
    var sec = item.querySelector('.pm-secondary');
    if (sec && !sec.complete) { sec.loading = 'eager'; }
  }, true);

  var active = null;

  function mediaOf(swatch) {
    var wrap = swatch.closest('.product-item-wrap');
    return wrap ? wrap.querySelector('.product-media') : null;
  }

  /* One plate per card, built the first time that card is previewed and then
     reused for every colour in its row. It lives inside .pm-hover so that the
     click handler in tiger.js clears it along with everything else; the next
     hover simply builds another one.

     Opacity is set here rather than in the stylesheet so that a plate can never
     be left opaque over the card by a stylesheet that failed to load. */
  function plateOf(media) {
    var hover = media.querySelector('.pm-hover');
    if (!hover) { return null; }
    var plate = hover.querySelector('.pm-preview');
    if (!plate) {
      plate = document.createElement('img');
      plate.className = 'pm-img pm-preview';
      plate.alt = '';
      plate.setAttribute('aria-hidden', 'true');
      plate.setAttribute('decoding', 'async');
      plate.style.opacity = '0';
      hover.appendChild(plate);
    }
    return plate;
  }

  function show(swatch) {
    var src = swatch.getAttribute('data-preview-src');
    if (!src) { return; }
    var media = mediaOf(swatch);
    if (!media) { return; }
    var plate = plateOf(media);
    if (!plate) { return; }

    plate.classList.toggle('is-worn', swatch.getAttribute('data-preview-worn') === '1');

    /* Reveal only once there is a frame to reveal. Fading in an image that has
       not arrived yet shows the plate's bare ground first, which reads as the
       card blanking out rather than as a photograph changing. This one can
       safely wait on the network: nothing is hidden until it arrives. */
    if (plate.getAttribute('src') === src && plate.complete && plate.naturalWidth) {
      plate.style.opacity = '1';
      return;
    }
    plate.style.opacity = '0';
    if (plate.getAttribute('src') !== src) { plate.setAttribute('src', src); }
    plate.addEventListener('load', function () {
      /* The cursor may well have moved on while this was in flight. */
      if (active === swatch) { plate.style.opacity = '1'; }
    }, { once: true });
  }

  function hide(swatch) {
    var media = mediaOf(swatch);
    var plate = media && media.querySelector('.pm-preview');
    if (plate) { plate.style.opacity = '0'; }
  }

  function move(swatch) {
    if (swatch === active) { return; }
    if (active) { hide(active); }
    active = swatch;
    if (swatch) { show(swatch); }
  }

  function swatchAt(node) {
    return node && node.closest ? node.closest('.swatches .swatch') : null;
  }

  /* pointerover, not pointerenter: one delegated listener then covers arriving
     at a swatch, crossing straight to the next one, and leaving the row for
     anything else on the page -- that last case arrives as a pointerover on
     whatever is under the cursor now, which closest() answers with null. */
  document.addEventListener('pointerover', function (e) {
    if (e.pointerType === 'touch') { return; }
    move(swatchAt(e.target));
  }, true);

  /* Leaving the window, or losing the pointer to a drag or a context menu,
     never produces that pointerover. */
  document.addEventListener('pointerleave', function () { move(null); });
  document.addEventListener('pointercancel', function () { move(null); });
  window.addEventListener('blur', function () { move(null); });

  /* Keyboard parity, the same bargain tiger.css strikes for the card itself in
     .product-item:focus-visible. :focus-visible and not :focus, because a
     swatch keeps focus after a click -- and previewing a colour over the card
     that click just swapped to it is nonsense. */
  document.addEventListener('focusin', function (e) {
    var swatch = swatchAt(e.target);
    if (!swatch) { return; }
    try {
      if (!swatch.matches(':focus-visible')) { return; }
    } catch (err) {
      return;
    }
    move(swatch);
  });
  document.addEventListener('focusout', function (e) {
    if (swatchAt(e.target) === active) { move(null); }
  });
})();
